import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FileText, Sparkle, Copy, CircleNotch } from "@phosphor-icons/react";

export function ExplorationSummaryDialog({ pid }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(null);
  const [at, setAt] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const r = await api.get(`/projects/${pid}/ai/exploration-summary`); setText(r.data.text || null); setAt(r.data.generated_at || null); }
    catch (e) { /* silent */ }
  };
  const generate = async () => {
    setBusy(true);
    try { const r = await api.post(`/projects/${pid}/ai/exploration-summary`); setText(r.data.text); setAt(r.data.generated_at); toast.success("Exploration summary generated"); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const copy = async () => { try { await navigator.clipboard.writeText(text || ""); toast.success("Copied"); } catch (e) { toast.error("Copy failed"); } };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="h-8" data-testid="ai-summary-open">
          <FileText size={14} className="sm:mr-1.5" /> <span className="hidden sm:inline">Exploration Summary</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl" data-testid="ai-summary-dialog">
        <DialogHeader><DialogTitle>AI Exploration Summary</DialogTitle></DialogHeader>
        <DialogDescription className="text-xs text-muted-foreground">A one-page technical synthesis of the project. It is automatically included on the Project Executive Summary page of the Master PDF report.</DialogDescription>
        {text ? (
          <div className="rounded-md border border-border bg-background p-4 max-h-[45vh] overflow-y-auto text-sm text-foreground whitespace-pre-wrap leading-relaxed" data-testid="ai-summary-text">{text}</div>
        ) : (
          <div className="rounded-md border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="ai-summary-empty">No summary yet. Generate one to include it in the Master PDF.</div>
        )}
        <div className="flex items-center gap-2">
          <Button onClick={generate} disabled={busy} data-testid="ai-summary-generate">
            {busy ? <><CircleNotch size={15} className="animate-spin mr-1.5" /> Generating…</> : <><Sparkle size={15} weight="fill" className="mr-1.5" style={{ color: "#A855F7" }} /> {text ? "Regenerate" : "Generate summary"}</>}
          </Button>
          {text && <Button variant="outline" onClick={copy} data-testid="ai-summary-copy"><Copy size={14} className="mr-1.5" /> Copy</Button>}
          {at && <span className="text-[10px] text-muted-foreground ml-auto">Updated {new Date(at).toLocaleString()}</span>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
